import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPushCapability, readPermission, PushCapability } from '../utils/pushSupport';

/**
 * 알림 권한 상태를 추적한다.
 *
 * Notification.permission은 값이 바뀌어도 이벤트를 쏘지 않는다. 사용자가 브라우저·OS 설정에
 * 다녀오는 것이 유일한 현실적 변경 경로라 visibilitychange에서 다시 읽고,
 * Permissions API가 있는 브라우저에서는 change 이벤트도 함께 받는다.
 */
export function useNotificationPermission(): {
  permission: NotificationPermission | 'unsupported';
  capability: PushCapability;
  refresh: () => void;
} {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => readPermission());
  // standalone 전환은 새 실행 컨텍스트로 시작되므로 마운트 시 1회 판정으로 충분하다.
  const capability = useMemo(() => getPushCapability(), []);

  const refresh = useCallback(() => setPermission(readPermission()), []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    let status: PermissionStatus | null = null;
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((s) => {
        status = s;
        s.onchange = refresh;
      })
      .catch(() => {
        // Permissions API가 없거나 'notifications'를 모르는 브라우저 — visibilitychange로 충분하다
      });

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (status) status.onchange = null;
    };
  }, [refresh]);

  return { permission, capability, refresh };
}
